// Copyright (c) StrangeBee 2023

import React, { ReactElement, useMemo } from 'react';
import { getAntdSelectOptions, useLocalMessage } from '@Helpers/';
import { useListAllFields } from '@Queries/describeQueries';
import { AllField } from '@Organisms/Dashboard/WidgetManage/AllField';
import { FormInstance } from 'antd/lib/form';
import { THForm } from '@Molecules/THForm/THForm';
import { THAutoComplete } from '@Molecules/Inputs/THAutoComplete/THAutoComplete';

import './EntityField.scss';

interface EntityFieldProps {
    form: FormInstance;
    showDate?: boolean;
    showPeriodDate?: boolean;
    showCategory?: boolean;
    entitiesToExclude?: string[];
}

const EntityField = ({
    form,
    showDate,
    showPeriodDate,
    showCategory,
    entitiesToExclude,
}: EntityFieldProps): ReactElement => {
    const i = useLocalMessage();
    const { data: describeData } = useListAllFields();
    const selectableEntities = useMemo(() => {
        const exclusionList = new Set(entitiesToExclude?.map((v) => v.toLowerCase()) || []);
        return Object.keys(describeData || {}).filter((entity) => !exclusionList.has(entity.toLowerCase()));
    }, [entitiesToExclude, describeData]);

    return (
        <div className="entity-field">
            <div className="entity-field__section">
                <THForm.Item
                    className="entity-field__section__item"
                    name="entity"
                    label={i('app.dashboard.widget.create.form.entity.label')}
                    rules={[
                        {
                            required: true,
                            message: i('app.dashboard.widget.create.form.entity.required'),
                            validator: (_, value) => {
                                if (describeData && !Object.keys(describeData).includes(value)) {
                                    return Promise.reject();
                                }

                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <THAutoComplete
                        placeholder={i('app.dashboard.widget.create.form.entity.placeholder')}
                        options={getAntdSelectOptions(selectableEntities)}
                    />
                </THForm.Item>

                {showCategory && (
                    <THForm.Item<any>
                        noStyle
                        shouldUpdate={(prevValues, nextValues) => prevValues['entity'] !== nextValues['entity']}
                    >
                        {() =>
                            describeData ? (
                                <AllField
                                    describeData={describeData}
                                    form={form}
                                    fieldType={[
                                        'user',
                                        'number',
                                        'string',
                                        'boolean',
                                        'enumeration',
                                        'float',
                                        'integer',
                                    ]}
                                    fieldName="category"
                                    formItemName="category"
                                    entity={form.getFieldValue('entity')}
                                    fieldsToExclude={['keyword']}
                                    indexTypeToExclude={['none']}
                                />
                            ) : null
                        }
                    </THForm.Item>
                )}
            </div>

            {(showDate || showPeriodDate) && (
                <div className="entity-field__section">
                    {showDate && (
                        <THForm.Item<any>
                            noStyle
                            shouldUpdate={(prevValues, nextValues): boolean =>
                                prevValues['entity'] !== nextValues['entity']
                            }
                        >
                            {() =>
                                describeData ? (
                                    <AllField
                                        describeData={describeData}
                                        fieldType={['date']}
                                        formItemName="dateField"
                                        fieldName="dateField"
                                        form={form}
                                        entity={form.getFieldValue('entity')}
                                        aggregationWarning={false}
                                    />
                                ) : null
                            }
                        </THForm.Item>
                    )}

                    {showPeriodDate && (
                        <THForm.Item<any>
                            noStyle
                            shouldUpdate={(prevValues, nextValues): boolean =>
                                prevValues['entity'] !== nextValues['entity']
                            }
                        >
                            {() =>
                                describeData ? (
                                    <AllField
                                        describeData={describeData}
                                        fieldType={['date']}
                                        formItemName="periodDateField"
                                        fieldName="periodDateField"
                                        form={form}
                                        entity={form.getFieldValue('entity')}
                                        aggregationWarning={false}
                                    />
                                ) : null
                            }
                        </THForm.Item>
                    )}
                </div>
            )}
        </div>
    );
};

export default EntityField;
